import { useState, useCallback, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const prefersReduced =
  typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

interface Slide {
  src: string;
  alt: string;
  label: string;
}

interface DashboardCarouselProps {
  slides: Slide[];
  autoPlayMs?: number;
}

export default function DashboardCarousel({ slides, autoPlayMs = 6000 }: DashboardCarouselProps) {
  const [index, setIndex] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const advance = useCallback(() => {
    setIndex(i => (i + 1) % slides.length);
  }, [slides.length]);

  const restart = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(advance, autoPlayMs);
  }, [advance, autoPlayMs]);

  useEffect(() => {
    timerRef.current = setInterval(advance, autoPlayMs);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [advance, autoPlayMs]);

  const go = (i: number) => {
    setIndex(i);
    restart();
  };

  const variants = {
    enter: { opacity: 0 },
    center: { opacity: 1 },
    exit: { opacity: 0 },
  };

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        maxWidth: 1300,
        margin: "0 auto",
        backgroundColor: "#123528",
        border: "1px solid #1E4838",
        borderRadius: 20,
        padding: 8,
      }}
    >
      {/* Image stage — clip-path rounds corners without overflow:hidden on positioned children */}
      <div
        style={{
          position: "relative",
          width: "100%",
          aspectRatio: "828/360",
          borderRadius: 12,
          overflow: "visible",
          clipPath: "inset(0 round 12px)",
          background: "#0A1A14",
        }}
      >
        <AnimatePresence initial={false} mode="wait">
          <motion.img
            key={slides[index].src}
            src={slides[index].src}
            alt={slides[index].alt}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.52, ease: [0.4, 0, 0.2, 1] }}
            style={{ display: "block", width: "100%", height: "100%", objectFit: "cover", objectPosition: "top center" }}
            draggable={false}
          />
        </AnimatePresence>

        {/* Slide progress bar */}
        {!prefersReduced && (
          <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 2, backgroundColor: "rgba(46,179,154,0.12)" }}>
            <motion.div
              key={`pb-${index}`}
              style={{ height: "100%", backgroundColor: "#2EB39A", originX: 0 }}
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: autoPlayMs / 1000, ease: "linear" }}
            />
          </div>
        )}
      </div>

      {/* Overlays — labels, controls, and scrim sit outside the image clip */}
      <div
        style={{
          position: "absolute",
          top: 8,
          right: 8,
          bottom: 8,
          left: 8,
          pointerEvents: "none",
        }}
      >
        <div
          aria-hidden
          style={{
            pointerEvents: "none",
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: 72,
            background: "linear-gradient(to top, rgba(8,22,16,.8) 0%, transparent 100%)",
          }}
        />

        <AnimatePresence mode="wait">
          <motion.div
            key={slides[index].label}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.32, delay: 0.18 }}
            style={{
              position: "absolute",
              bottom: 16,
              left: 16,
              display: "inline-flex",
              alignItems: "center",
              gap: 9,
              fontSize: 12.5,
              fontWeight: 600,
              color: "#EAF3EE",
              padding: "8px 14px 8px 12px",
              borderRadius: 100,
              backgroundColor: "#0D2B22",
              border: "1px solid #1E4838",
              pointerEvents: "auto",
            }}
          >
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                backgroundColor: "#2EB39A",
                boxShadow: "0 0 10px #2EB39A",
                flexShrink: 0,
                display: "block",
              }}
            />
            {slides[index].label}
          </motion.div>
        </AnimatePresence>

        <div
          style={{
            position: "absolute",
            bottom: 16,
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            alignItems: "center",
            gap: 8,
            pointerEvents: "auto",
          }}
        >
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Slide ${i + 1}`}
              onClick={() => go(i)}
              style={{
                height: 8,
                width: i === index ? 28 : 8,
                borderRadius: 100,
                backgroundColor: i === index ? "#2EB39A" : "rgba(255,255,255,.32)",
                border: "none",
                cursor: "pointer",
                transition: "all 0.3s cubic-bezier(0.22,1,0.36,1)",
                padding: 0,
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
